const VALIDATED_PRINTER_KEY = 'roomnexa.restaurant.validatedPrinterName';
const PRINTER_WIDTH_KEY = 'roomnexa.restaurant.printerWidthMm';

const DESKTOP_UA_PATTERN = /RoomNexaDesktop\//i;

/** True only inside the Electron desktop shell (preload exposes window.desktop). */
export function isDesktopApp() {
  return typeof window !== 'undefined' && Boolean(window.desktop?.isDesktop);
}

export function hasDesktopPrintApi() {
  return (
    isDesktopApp()
    && (
      typeof window.desktop?.print?.silentEscPosHtml === 'function'
      || typeof window.desktop?.print?.silentHtml === 'function'
    )
  );
}

/** Desktop app shell detected via UA but preload/IPC print API is missing. */
export function isDesktopShellMissingApis() {
  return DESKTOP_UA_PATTERN.test(navigator.userAgent || '') && !hasDesktopPrintApi();
}

export function getValidatedPrinterName() {
  try {
    return String(window.localStorage.getItem(VALIDATED_PRINTER_KEY) || '').trim();
  } catch (_err) {
    return '';
  }
}

function normalizeThermalWidth(width) {
  return Number(width) === 58 ? 58 : 80;
}

function inferPaperWidthFromPrinterName(printerName = '') {
  const name = String(printerName || '').toLowerCase();
  if (/\b58\b|58mm|2[\s-]*inch|2inch/.test(name)) return 58;
  if (/\b80\b|80mm|3[\s-]*inch|3inch/.test(name)) return 80;
  return null;
}

function inferPaperWidthMmFromHtml(html) {
  const match = String(html || '').match(/@page\s*\{[^}]*size\s*:\s*(\d+)mm/i);
  return Number(match?.[1]) === 58 ? 58 : 80;
}

function isKotPrintHtml(html) {
  return /<title>\s*KOT\b/i.test(String(html || ''));
}

function injectDesktopPrintCss(html, cssBlock) {
  if (!cssBlock) return html;
  if (/<\/head>/i.test(html)) {
    return html.replace(/<\/head>/i, `${cssBlock}</head>`);
  }
  return `${cssBlock}${html}`;
}

function rewriteHtmlThermalWidth(html, widthMm) {
  const width = normalizeThermalWidth(widthMm);
  return String(html || '')
    .replace(/(@page\s*\{[^}]*?size\s*:\s*)\d+mm/gi, `$1${width}mm`)
    .replace(/(@page\s*\{[^}]*?margin\s*:\s*)[^;]+;/gi, '$10;')
    .replace(/(body\s*\{[^}]*?width\s*:\s*)\d+mm/gi, `$1${width}mm`)
    .replace(/(@media print\s*\{[^}]*?html,\s*body\s*\{[^}]*?width\s*:\s*)\d+mm/gi, `$1${width}mm`)
    .replace(/(\.preview-toolbar\s*\{[^}]*?width\s*:\s*)\d+mm/gi, `$1${width}mm`);
}

function buildDesktopThermalWidthCss(widthMm) {
  const width = normalizeThermalWidth(widthMm);
  return `
    <style id="roomnexa-desktop-thermal-width">
      @page { size: ${width}mm auto !important; margin: 0 !important; }
      html, body {
        width: ${width}mm !important;
        max-width: ${width}mm !important;
        min-width: ${width}mm !important;
        margin: 0 !important;
        padding: 1mm !important;
        box-sizing: border-box !important;
      }
      table, .meta-grid, .receipt-row {
        width: 100% !important;
        max-width: 100% !important;
      }
      @media print {
        html, body {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          margin: 0 !important;
          padding: 1mm !important;
          box-sizing: border-box !important;
        }
        table, .meta-grid, .receipt-row {
          width: 100% !important;
          max-width: 100% !important;
        }
      }
    </style>`;
}

/** Desktop KOT/invoice shared width — layout/fonts unchanged for KOT. */
function applyDesktopThermalWidthToHtml(html, widthMm) {
  const width = normalizeThermalWidth(widthMm);
  const rewritten = rewriteHtmlThermalWidth(html, width);
  return injectDesktopPrintCss(rewritten, buildDesktopThermalWidthCss(width));
}

/** Desktop invoice: edge-to-edge thermal layout, logo kept, GSTIN/tax markup unchanged. */
function enhanceDesktopInvoiceHtml(html) {
  if (isKotPrintHtml(html)) return html;

  const widthMm = getThermalPaperWidthMm();
  const is58 = widthMm === 58;
  const bodyFont = is58 ? '12px' : '14px';
  const titleFont = is58 ? '16px' : '18px';
  const grandFont = is58 ? '15px' : '18px';
  const logoMax = is58 ? '28mm' : '36mm';

  const widthApplied = applyDesktopThermalWidthToHtml(html, widthMm);

  const css = `
    <style id="roomnexa-desktop-invoice-scale">
      html, body {
        width: ${widthMm}mm !important;
        max-width: ${widthMm}mm !important;
        min-width: ${widthMm}mm !important;
        margin: 0 !important;
        padding: 1mm !important;
        box-sizing: border-box !important;
        color: #000 !important;
        font-weight: 700 !important;
        line-height: 1.35 !important;
        font-size: ${bodyFont} !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
      }
      @media print {
        html, body {
          width: 100% !important;
          max-width: 100% !important;
          min-width: 0 !important;
          margin: 0 !important;
          padding: 1mm !important;
        }
      }
      .logo {
        display: block !important;
        max-width: ${logoMax} !important;
        max-height: 18mm !important;
        object-fit: contain !important;
        margin: 0 auto 3px !important;
        filter: grayscale(1) contrast(1.25) !important;
      }
      .title {
        font-size: ${titleFont} !important;
        font-weight: 900 !important;
      }
      .sub-title {
        font-size: ${is58 ? '13px' : '14px'} !important;
        font-weight: 800 !important;
      }
      .grand {
        font-size: ${grandFont} !important;
        font-weight: 900 !important;
      }
      th, td, .receipt-row, .meta-grid, .bold {
        font-size: ${bodyFont} !important;
        font-weight: 700 !important;
      }
      .meta-grid .bold {
        font-weight: 900 !important;
      }
      .muted {
        font-size: ${is58 ? '11px' : '12px'} !important;
        font-weight: 600 !important;
      }
      table {
        table-layout: fixed !important;
        width: 100% !important;
      }
      th:nth-child(1), td:nth-child(1) {
        width: 46% !important;
        word-break: break-word !important;
        text-align: left !important;
      }
      th:nth-child(2), td:nth-child(2) { width: 14% !important; }
      th:nth-child(3), td:nth-child(3) { width: 20% !important; }
      th:nth-child(4), td:nth-child(4) { width: 20% !important; }
      .num {
        text-align: right !important;
        white-space: nowrap !important;
        padding-left: 1px !important;
        font-weight: 700 !important;
      }
      .receipt-row {
        width: 100% !important;
        justify-content: space-between !important;
        gap: 4px !important;
      }
      .receipt-row span:first-child {
        max-width: 68% !important;
      }
      .receipt-row span:last-child {
        text-align: right !important;
        white-space: nowrap !important;
        font-weight: 800 !important;
      }
      .line { margin: 4px 0 !important; }
    </style>`;

  return injectDesktopPrintCss(widthApplied, css);
}

function enhanceDesktopTestPrintHtml(html, widthMm) {
  const width = normalizeThermalWidth(widthMm || getThermalPaperWidthMm());
  const is58 = width === 58;
  const widthApplied = applyDesktopThermalWidthToHtml(html, width);
  const css = `
    <style id="roomnexa-desktop-test-print-scale">
      body {
        font-size: ${is58 ? '12px' : '14px'} !important;
        font-weight: 700 !important;
        line-height: 1.4 !important;
        padding: 1mm !important;
      }
      .title { font-size: ${is58 ? '18px' : '20px'} !important; font-weight: 900 !important; }
    </style>`;
  return injectDesktopPrintCss(widthApplied, css);
}

export function saveValidatedPrinterName(printerName) {
  const name = String(printerName || '').trim();
  if (!name) return;
  try {
    window.localStorage.setItem(VALIDATED_PRINTER_KEY, name);
  } catch (_err) {
    // ignore quota errors
  }
}

export function clearValidatedPrinterName() {
  try {
    window.localStorage.removeItem(VALIDATED_PRINTER_KEY);
  } catch (_err) {
    // ignore
  }
}

export function isPrinterValidated() {
  if (!hasDesktopPrintApi()) return true;
  return Boolean(getValidatedPrinterName());
}

export function getThermalPaperWidthMm(printerName = '') {
  const fallbackFromName = inferPaperWidthFromPrinterName(printerName || getValidatedPrinterName());
  try {
    const stored = window.localStorage.getItem(PRINTER_WIDTH_KEY);
    if (stored !== null && stored !== undefined && String(stored).trim() !== '') {
      return normalizeThermalWidth(Number(stored));
    }
    return normalizeThermalWidth(fallbackFromName || 80);
  } catch (_err) {
    return normalizeThermalWidth(fallbackFromName || 80);
  }
}

export function saveThermalPaperWidthMm(widthMm) {
  const width = normalizeThermalWidth(widthMm);
  try {
    window.localStorage.setItem(PRINTER_WIDTH_KEY, String(width));
  } catch (_err) {
    // ignore
  }
}

export function suggestThermalPaperWidthForPrinter(printerName) {
  return normalizeThermalWidth(inferPaperWidthFromPrinterName(printerName) || 80);
}

/** @deprecated Use saveThermalPaperWidthMm after explicit user selection. */
export function syncThermalPaperWidthForPrinter(printerName) {
  const widthMm = suggestThermalPaperWidthForPrinter(printerName);
  saveThermalPaperWidthMm(widthMm);
  return widthMm;
}

export async function listDesktopPrinters() {
  if (!isDesktopApp() || !window.desktop?.print?.getPrinters) {
    return [];
  }
  try {
    const printers = await window.desktop.print.getPrinters();
    return Array.isArray(printers) ? printers : [];
  } catch (_err) {
    return [];
  }
}

export function openPrinterValidationDialog() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('roomnexa:open-printer-validation'));
  }
}

async function silentPrintHtmlPayload(html, deviceName, options = {}) {
  if (typeof window.desktop?.print?.silentHtml !== 'function') {
    return { success: false, error: 'Desktop silent HTML print is not available.' };
  }
  return window.desktop.print.silentHtml({
    html,
    deviceName,
    printBackground: options.printBackground ?? true,
  });
}

async function silentPrintEscPosPayload(html, deviceName, options = {}) {
  if (typeof window.desktop?.print?.silentEscPosHtml !== 'function') {
    return { success: false, error: 'Desktop ESC/POS print is not available.' };
  }
  return window.desktop.print.silentEscPosHtml({
    html,
    deviceName,
    printBackground: options.printBackground ?? true,
  });
}

async function isWindowsDesktopShell() {
  try {
    const info = await window.desktop?.platform?.get?.();
    return String(info?.platform || '').toLowerCase() === 'win32';
  } catch (_err) {
    return false;
  }
}

/** Inline logo <img> as data URL so Electron ESC/POS can rasterize without a second network hop. */
async function inlineLogoDataUrls(html) {
  const srcMatch = html.match(
    /<img[^>]*class=["'][^"']*logo[^"']*["'][^>]*src=["']([^"']+)["']/i
  ) || html.match(
    /<img[^>]*src=["']([^"']+)["'][^>]*class=["'][^"']*logo[^"']*["']/i
  );
  const src = srcMatch?.[1]?.trim();
  if (!src || src.startsWith('data:')) return html;

  try {
    const dataUrl = await new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      const timer = window.setTimeout(() => reject(new Error('logo load timeout')), 2500);
      img.onload = () => {
        window.clearTimeout(timer);
        try {
          const canvas = document.createElement('canvas');
          const maxW = 320;
          const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
          canvas.width = Math.max(1, Math.round(img.naturalWidth * scale));
          canvas.height = Math.max(1, Math.round(img.naturalHeight * scale));
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('canvas unavailable'));
            return;
          }
          ctx.fillStyle = '#fff';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/png'));
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = () => {
        window.clearTimeout(timer);
        reject(new Error('logo load failed'));
      };
      img.src = src;
    });
    return html.replace(src, dataUrl);
  } catch (_err) {
    return html;
  }
}

export async function silentPrintRestaurantHtml(html, options = {}) {
  if (!isDesktopApp()) {
    return { success: false, error: 'Desktop silent print is not available.' };
  }
  const deviceName = String(options.deviceName || getValidatedPrinterName() || '').trim();
  if (!deviceName) {
    return { success: false, error: 'No validated printer selected.' };
  }

  const explicitKind = options.printKind;
  const printKind = explicitKind || (isKotPrintHtml(html) ? 'kot' : 'bill');
  const widthMm = getThermalPaperWidthMm(deviceName);

  let payloadHtml = html;
  if (printKind === 'bill') {
    payloadHtml = enhanceDesktopInvoiceHtml(html);
  } else if (printKind === 'test') {
    payloadHtml = enhanceDesktopTestPrintHtml(html, options.paperWidthMm ?? widthMm);
  } else if (printKind === 'kot') {
    payloadHtml = applyDesktopThermalWidthToHtml(html, widthMm);
  }

  try {
    // CN811 / generic thermals on Windows need ESC/POS RAW. Chromium HTML
    // jobs are slow and often only feed+cut a blank strip — do not fall back.
    const preferEscPos = options.preferEscPos !== false;
    const onWindows = await isWindowsDesktopShell();
    if (preferEscPos && onWindows) {
      const withLogo = await inlineLogoDataUrls(payloadHtml);
      return silentPrintEscPosPayload(withLogo, deviceName, options);
    }

    return await silentPrintHtmlPayload(payloadHtml, deviceName, options);
  } catch (error) {
    return {
      success: false,
      error: error?.message || 'Silent print failed.',
    };
  }
}

export function buildPrinterTestHtml(paperWidthMm = null, printerName = '') {
  const width = normalizeThermalWidth(paperWidthMm || getThermalPaperWidthMm(printerName));
  const now = new Date().toLocaleString();
  return `<!doctype html>
<html><head><meta charset="utf-8" />
<title>Printer Test</title>
<style>
  @page { size: ${width}mm auto; margin: 0; }
  body { width: ${width}mm; margin: 0; padding: 1mm; font-family: "Courier New", monospace; font-size: 12px; }
  .center { text-align: center; }
  .title { font-size: 16px; font-weight: 800; }
  .line { border-top: 1px dashed #000; margin: 6px 0; }
</style></head><body>
  <div class="center title">RoomNexa</div>
  <div class="center">INVOICE / KOT TEST</div>
  <div class="line"></div>
  <div class="center">Printer test OK (${width}mm)</div>
  <div class="center">${now}</div>
  <div class="line"></div>
  <div>Item           Qty  Amount</div>
  <div>Test Item        1   100.00</div>
  <div class="line"></div>
  <div class="center">Thanks</div>
</body></html>`;
}

export async function getDefaultDesktopPrinter() {
  if (!isDesktopApp() || !window.desktop?.print?.getDefaultPrinter) {
    return null;
  }
  try {
    return await window.desktop.print.getDefaultPrinter();
  } catch (_err) {
    return null;
  }
}
